import {
    useState, useMemo, useCallback,
} from 'react'
import {
    useLocation,
} from 'react-router-dom'

import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'

import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import Button from 'app/shared-components/Button'
import {
    AQuote,
} from '../AQuotes.types'

import QuotationOptions from '../QuotationOptions'
import AQuoteInformation from '../AQuoteInformation'
import {
    AQuoteInformationContext,
} from './AQuoteInformationContext'
import useAQuoteCreate from './hooks/useAQuoteCreate'
import useStyles from './AQuoteCreate.styles'

const AQuoteCreate = () => {
    const {
        classes,
    } = useStyles()
    const {
        state,
    } = useLocation()
    const [
        value,
        setValue,
    ] = useState<AQuote>(state || {} as AQuote)
    const {
        AQuotes: AQuotesRoutes,
    } = useAccountManagementRoutes()

    const [
        isEdited,
        setIsEdited,
    ] = useState<boolean>()

    useBeforeUnloadDialog(isEdited)

    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(0)

    const onSuccess = useCallback(({
        id,
    }) => {
        AQuotesRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [AQuotesRoutes.Overview])

    const createAQuote = useAQuoteCreate({
        onSuccess,
        setIsEdited,
    })

    const getActionButtons = useCallback(({
        onNext, disabled = false, isValid = true,
    }) => {
        return [(
            <div
                key="action-buttons"
                className={classes.buttonWrapper}
            >
                <Button
                    label="Next"
                    onClick={onNext}
                    className={classes.button}
                    disabled={disabled || !isValid}
                    data-testid="btn-next"
                />
            </div>)]
    }, [classes])

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Fill Quote Information',
                expanded: true,
                content: <AQuoteInformation
                    data={value}
                    disabled={activeStep !== 0}
                    getActionButtons={getActionButtons}
                />,
            },
            {
                key: '2',
                label: 'Specify Quotation Options',
                expanded: (step: number) => {
                    return step > 0
                },
                content: <QuotationOptions
                    data={value}
                    actionQuote={createAQuote}
                />,
            },
        ]
    }, [
        value,
        activeStep,
        getActionButtons,
        createAQuote,
    ])

    const aQuoteInformationContext = useMemo(() => {
        return {
            activeStep,
            setActiveStep,
            value,
            setValue,
            setIsEdited,
            isEdited,
        }
    }, [
        activeStep,
        setActiveStep,
        value,
        setValue,
        isEdited,
    ])

    return (
        <AQuoteInformationContext.Provider value={aQuoteInformationContext}>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={isEdited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </AQuoteInformationContext.Provider>
    )
}

export default AQuoteCreate
